// Logica della scheda Report Mapping di Projects V2.
// È la stessa della pagina classica (pages/ProjectDetail.js), copiata senza modifiche: stati, chiamate API,
// validazioni e messaggi restano identici; cambia solo l'interfaccia (ProjectReportMapping.jsx).
// Se si cambia una regola del mapping, va cambiata in entrambi i punti finché la pagina classica esiste.
// Unica aggiunta rispetto alla classica: mappingValuesCountByField (numero di Mapping Values per campo), solo per la visualizzazione.
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../../../config/api.config';

export default function useReportMapping(projectId) {
  const id = projectId;

  // Mapping tab state (nuovo modello RUNR)
  const [selectedDataSource, setSelectedDataSource] = useState('');
  // savedDataSourceType: ciò che è effettivamente persisito in DB ('' = non salvato).
  const [savedDataSourceType, setSavedDataSourceType] = useState('');
  const [isMasterServiceDraft, setIsMasterServiceDraft] = useState(false);
  const [savedIsMasterService, setSavedIsMasterService] = useState(false);
  const [jiraProjectIdDraft, setJiraProjectIdDraft] = useState('');
  const isEditingJiraProjectIdRef = useRef(false);
  const jiraProjectIdDirtyRef = useRef(false);
  const mappingFormInitializedRef = useRef(false);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingSaving, setMappingSaving] = useState(false);
  const [mappingError, setMappingError] = useState(null);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [mappingDialogType, setMappingDialogType] = useState('field');
  const [editingSheetMappingId, setEditingSheetMappingId] = useState(null);
  const [mappingDialogError, setMappingDialogError] = useState(null);
  const [mappingDialogForm, setMappingDialogForm] = useState({
    file_url: '',
    file_name: '',
    ticketing_system: '',
    data_structure: '',
  });
  const [dataStructureOptions, setDataStructureOptions] = useState([
    { value: 'column', label: 'Column' },
    { value: 'row', label: 'Row' },
  ]);
  const [mappingItems, setMappingItems] = useState([]);
  const [ticketingOptions, setTicketingOptions] = useState([
    { value: 'servicenow', label: 'ServiceNow' },
    { value: 'remedy', label: 'Remedy' },
    { value: 'jira', label: 'Jira' },
    { value: 'other', label: 'Other' },
  ]);
  const [mappingLogicOptions, setMappingLogicOptions] = useState([
    { value: 'move', label: 'Move' },
    { value: 'mapping', label: 'Mapping' },
    { value: 'choice', label: 'Choice' },
    { value: 'constant', label: 'Constant' },
    { value: 'format', label: 'Format' },
    { value: 'dimension', label: 'Dimension' },
  ]);
  const [configDialogLoading, setConfigDialogLoading] = useState(false);
  const [configDialogSavingAll, setConfigDialogSavingAll] = useState(false);
  const [configDialogError, setConfigDialogError] = useState(null);
  const [selectedSheetMapping, setSelectedSheetMapping] = useState(null);
  const [configRows, setConfigRows] = useState([]);
  const [configInitialSnapshot, setConfigInitialSnapshot] = useState('');
  const [configSearchTerm, setConfigSearchTerm] = useState('');
  const [configFilter, setConfigFilter] = useState('all');
  const [mappingToast, setMappingToast] = useState({ open: false, message: '', severity: 'success' });
  const [mappingValuesDialogOpen, setMappingValuesDialogOpen] = useState(false);
  const [mappingValuesLoading, setMappingValuesLoading] = useState(false);
  const [mappingValuesError, setMappingValuesError] = useState(null);
  const [mappingValues, setMappingValues] = useState([]);
  const [missingMappingAlertByField, setMissingMappingAlertByField] = useState({});
  // Solo V2 (visualizzazione): quanti Mapping Values ha ogni campo, letti negli stessi punti dell'avviso "Not configured"
  const [mappingValuesCountByField, setMappingValuesCountByField] = useState({});
  const rememberValuesCount = (fieldKey, values) =>
    setMappingValuesCountByField((prev) => ({ ...prev, [fieldKey]: Array.isArray(values) ? values.length : 0 }));
  // field_key del campo con help aperto al click (resta aperto finche non si clicca fuori).
  const [pinnedHelpFieldKey, setPinnedHelpFieldKey] = useState(null);
  const [hoveredHelpFieldKey, setHoveredHelpFieldKey] = useState(null);
  const [selectedConfigRowForValues, setSelectedConfigRowForValues] = useState(null);
  const [normalizedOptionsForValues, setNormalizedOptionsForValues] = useState([]);
  const [mappingValueForm, setMappingValueForm] = useState({
    id: null,
    source_value: '',
    normalized_ref_id: '',
  });
  const [formatHelpOpen, setFormatHelpOpen] = useState(false);
  // Opzioni lookup per dominio di ref_lookup_values (choice: lookup_domain || field_key,
  // dimension: lookup_domain || 'dimension').
  const [lookupOptionsByDomain, setLookupOptionsByDomain] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogTitle, setConfirmDialogTitle] = useState('');
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
  const [confirmDialogAction, setConfirmDialogAction] = useState(null);
  const [confirmDialogBusy, setConfirmDialogBusy] = useState(false);


  const handleDataSourceChange = (event) => {
    const newValue = event.target.value;
    setSelectedDataSource(newValue);
    mappingFormInitializedRef.current = true;
    setMappingError(null);
    if (newValue !== 'jira') {
      setJiraProjectIdDraft('');
      jiraProjectIdDirtyRef.current = false;
    }
  };

  const handleMasterServiceChange = (event) => {
    setIsMasterServiceDraft(event.target.checked);
    mappingFormInitializedRef.current = true;
    setMappingError(null);
  };

  const openAddMappingDialog = (type) => {
    setMappingDialogType(type);
    setEditingSheetMappingId(null);
    setMappingDialogForm({ file_url: '', file_name: '', ticketing_system: '', data_structure: '' });
    setMappingDialogError(null);
    setMappingDialogOpen(true);
  };

  const handleAddFieldMapping = async () => {
    if (!savedIsMasterService) {
      setMappingError('Master service must be enabled to add sheet mappings.');
      return;
    }
    // Auto-save data source if it hasn't been persisted yet.
    if (savedDataSourceType !== 'gsheet') {
      const ok = await handleSaveDataSource();
      if (!ok) return; // Error already set by handleSaveDataSource.
    }
    openAddMappingDialog('field');
  };

  const handleAddSlaMapping = async () => {
    if (!savedIsMasterService) {
      setMappingError('Master service must be enabled to add sheet mappings.');
      return;
    }
    // Auto-save data source if it hasn't been persisted yet.
    if (savedDataSourceType !== 'gsheet') {
      const ok = await handleSaveDataSource();
      if (!ok) return; // Error already set by handleSaveDataSource.
    }
    openAddMappingDialog('sla');
  };

  const handleCloseMappingDialog = () => {
    setMappingDialogOpen(false);
    setEditingSheetMappingId(null);
    setMappingDialogError(null);
  };

  const handleMappingDialogChange = (e) => {
    const { name, value } = e.target;
    setMappingDialogForm((prev) => ({ ...prev, [name]: value }));
  };

  const parseListPayload = (data, keys = []) => {
    for (const key of keys) {
      if (Array.isArray(data?.[key])) return data[key];
    }
    if (Array.isArray(data)) return data;
    return [];
  };

  const fetchMappingData = useCallback(async () => {
    if (!id) return;
    setMappingLoading(true);
    setMappingError(null);
    try {
      const [dataSourceRes, sheetMappingRes, ticketingRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.DATA_SOURCE}?project_id=${id}`),
        fetch(`${API_ENDPOINTS.SHEET_MAPPING}?project_id=${id}`),
        fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=ticketing_system`),
      ]);

      if (!dataSourceRes.ok) {
        throw new Error(`Unable to load data source (${dataSourceRes.status})`);
      }
      if (!sheetMappingRes.ok) {
        throw new Error(`Unable to load sheet mappings (${sheetMappingRes.status})`);
      }

      const dsPayload = await dataSourceRes.json();
      const sheetPayload = await sheetMappingRes.json();

      const dataSourceCandidates = parseListPayload(dsPayload, ['items', 'data_sources', 'data']);
      const activeSource = dataSourceCandidates.find((row) => row?.is_active !== false) || dataSourceCandidates[0] || dsPayload?.item || dsPayload?.data_source || null;
      const dataSourceType = activeSource?.data_source_type?.toLowerCase() || '';
      const masterService = !!activeSource?.is_master_service;
      setSavedDataSourceType(dataSourceType);
      setSavedIsMasterService(masterService);
      if (!mappingFormInitializedRef.current) {
        if (dataSourceType) {
          setSelectedDataSource(dataSourceType);
          setJiraProjectIdDraft(activeSource?.jira_project_id || '');
          setIsMasterServiceDraft(masterService);
        } else {
          setSelectedDataSource('');
          setJiraProjectIdDraft('');
          setIsMasterServiceDraft(false);
        }
        mappingFormInitializedRef.current = true;
      } else {
        setIsMasterServiceDraft(masterService);
      }

      if (!masterService) {
        resetConfigPanel();
        setMappingValuesDialogOpen(false);
        setSelectedConfigRowForValues(null);
        setMappingValues([]);
        setMappingValuesError(null);
      }

      const rawMappings = parseListPayload(sheetPayload, ['items', 'sheet_mappings', 'data']);
      const mappedItems = rawMappings.map((item) => ({
        id: item.id,
        mapping_type: item.mapping_type,
        file_url: item.file_url,
        file_name: item.file_name,
        ticketing_system: item.ticketing_system,
        is_draft: item.is_draft,
        data_structure: item.data_structure,
      }));
      setMappingItems(mappedItems);

      setSelectedSheetMapping((prev) => {
        if (!prev) return prev;
        const stillExists = mappedItems.some((item) => item.id === prev.id);
        if (stillExists) return prev;

        // If selected mapping was deleted, close configuration panel and related dialogs/state.
        setConfigRows([]);
        setConfigDialogError(null);
        setConfigInitialSnapshot('');
        setConfigSearchTerm('');
        setConfigFilter('all');
        setMappingValuesDialogOpen(false);
        setSelectedConfigRowForValues(null);
        setMappingValues([]);
        setNormalizedOptionsForValues([]);
        setMappingValuesError(null);
        return null;
      });

      if (ticketingRes.ok) {
        const lookupPayload = await ticketingRes.json();
        const lookupItems = parseListPayload(lookupPayload, ['items', 'values', 'data']);
        if (lookupItems.length > 0) {
          setTicketingOptions(
            lookupItems.map((row) => ({
              value: row.value,
              label: row.label || row.value,
            }))
          );
        }
      }

      try {
        const mappingLogicRes = await fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=mapping_logic`);
        if (mappingLogicRes.ok) {
          const mappingLogicPayload = await mappingLogicRes.json();
          const mappingLogicItems = parseListPayload(mappingLogicPayload, ['items', 'values', 'data']);
          if (mappingLogicItems.length > 0) {
            setMappingLogicOptions(
              mappingLogicItems.map((row) => ({
                value: row.value,
                label: row.label || row.value,
              }))
            );
          }
        }
      } catch {
        // fallback to default static options
      }

      try {
        const dataStructureRes = await fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=data_structure`);
        if (dataStructureRes.ok) {
          const dataStructurePayload = await dataStructureRes.json();
          const dataStructureItems = parseListPayload(dataStructurePayload, ['items', 'values', 'data']);
          if (dataStructureItems.length > 0) {
            setDataStructureOptions(
              dataStructureItems.map((row) => ({
                value: row.value,
                label: row.label || row.value,
              }))
            );
          }
        }
      } catch {
        // fallback to default static options
      }
    } catch (e) {
      setMappingError(e.message || 'Unable to load mapping configuration.');
    } finally {
      setMappingLoading(false);
    }
  }, [id]);

  const handleSaveDataSource = async () => {
    if (!id) return false;
    if (!selectedDataSource) {
      setMappingError('Please select a Data Source.');
      return false;
    }
    if (isMasterServiceDraft && selectedDataSource === 'jira' && !jiraProjectIdDraft.trim()) {
      setMappingError('Jira Project ID is required when Data Source is JIRA and Master service is enabled.');
      return false;
    }

    setMappingSaving(true);
    setMappingError(null);
    try {
      const response = await fetch(API_ENDPOINTS.DATA_SOURCE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: parseInt(id, 10),
          data_source_type: selectedDataSource,
          is_master_service: isMasterServiceDraft,
          jira_project_id: isMasterServiceDraft && selectedDataSource === 'jira'
            ? jiraProjectIdDraft.trim()
            : null,
          is_active: true,
        }),
      });
      if (!response.ok) {
        throw new Error((await response.text()) || `Save failed (${response.status})`);
      }
      jiraProjectIdDirtyRef.current = false;
      setSavedDataSourceType(selectedDataSource);
      setSavedIsMasterService(isMasterServiceDraft);
      if (!isMasterServiceDraft) {
        resetConfigPanel();
        setMappingValuesDialogOpen(false);
        setSelectedConfigRowForValues(null);
        setMappingValues([]);
        setMappingValuesError(null);
      }
      await fetchMappingData();
      return true;
    } catch (e) {
      setMappingError(e.message || 'Unable to save the data source.');
      return false;
    } finally {
      setMappingSaving(false);
    }
  };

  const handleSaveMappingDialog = () => {
    if (!id) return;
    if (!savedIsMasterService) {
      setMappingDialogError('Master service must be enabled to modify sheet mappings.');
      return;
    }
    const fileUrl = mappingDialogForm.file_url.trim();
    const fileName = mappingDialogForm.file_name.trim();
    const isSlaDialog = mappingDialogType === 'sla';
    // SLA mapping: Ticketing System non previsto; Data Structure obbligatoria (fissata alla creazione).
    const ticketingSystem = isSlaDialog ? '' : mappingDialogForm.ticketing_system.trim();
    const dataStructure = mappingDialogForm.data_structure;

    if (!fileUrl || !fileName || (!isSlaDialog && !ticketingSystem) || (isSlaDialog && !dataStructure)) {
      setMappingDialogError('Please fill in all required fields.');
      return;
    }

    // Frontend-only validation for now (backend not available yet).
    if (!/^https?:\/\/.+/i.test(fileUrl)) {
      setMappingDialogError('File URL must be a valid http/https URL.');
      return;
    }

    setMappingSaving(true);
    fetch(API_ENDPOINTS.SHEET_MAPPING, {
      method: editingSheetMappingId ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...(editingSheetMappingId ? { id: editingSheetMappingId } : {
          project_id: parseInt(id, 10),
          mapping_type: mappingDialogType,
          ...(isSlaDialog ? { data_structure: dataStructure } : {}),
        }),
        file_url: fileUrl,
        file_name: fileName,
        ticketing_system: ticketingSystem || null,
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error((await response.text()) || `Save failed (${response.status})`);
        }
        setMappingDialogOpen(false);
        setMappingDialogError(null);
        await fetchMappingData();
      })
      .catch((e) => {
        setMappingDialogError(e.message || 'Unable to save mapping.');
      })
      .finally(() => {
        setMappingSaving(false);
      });
  };

  const handleEditSheetMapping = (item) => {
    setEditingSheetMappingId(item.id);
    setMappingDialogType(item.mapping_type);
    setMappingDialogForm({
      file_url: item.file_url || '',
      file_name: item.file_name || '',
      ticketing_system: item.ticketing_system || '',
      data_structure: item.data_structure || '',
    });
    setMappingDialogError(null);
    setMappingDialogOpen(true);
  };

  const handleDeleteSheetMapping = async (item) => {
    if (!savedIsMasterService) {
      setMappingError('Master service must be enabled to delete sheet mappings.');
      return;
    }
    setConfirmDialogTitle('Delete mapping');
    setConfirmDialogMessage(`Delete mapping "${item.file_name}"?`);
    setConfirmDialogAction(() => async () => {
      setMappingSaving(true);
      try {
        const response = await fetch(API_ENDPOINTS.SHEET_MAPPING, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.id }),
        });
        if (!response.ok) {
          throw new Error((await response.text()) || `Delete failed (${response.status})`);
        }
        await fetchMappingData();
        setMappingToast({ open: true, message: 'Mapping deleted successfully.', severity: 'success' });
      } catch (e) {
        setMappingError(e.message || 'Unable to delete mapping.');
        setMappingToast({ open: true, message: 'Unable to delete the mapping.', severity: 'error' });
      } finally {
        setMappingSaving(false);
      }
    });
    setConfirmDialogOpen(true);
  };

  const resetConfigPanel = () => {
    setSelectedSheetMapping(null);
    setConfigRows([]);
    setConfigDialogError(null);
    setConfigInitialSnapshot('');
    setConfigSearchTerm('');
    setConfigFilter('all');
    setMissingMappingAlertByField({});
    setMappingValuesCountByField({});
  };

  const handleOpenConfigDialog = async (sheetMapping) => {
    setSelectedSheetMapping(sheetMapping);
    setConfigDialogLoading(true);
    setConfigDialogError(null);
    try {
      const [defsRes, cfgRes] = await Promise.all([
        fetch(
          `${API_ENDPOINTS.FIELD_DEFINITIONS}?applies_to=${sheetMapping.mapping_type}` +
          (sheetMapping.data_structure ? `&data_structure=${encodeURIComponent(sheetMapping.data_structure)}` : '')
        ),
        fetch(`${API_ENDPOINTS.FIELD_CONFIG}?sheet_mapping_id=${sheetMapping.id}`),
      ]);
      if (!defsRes.ok) {
        throw new Error((await defsRes.text()) || `Unable to load field definitions (${defsRes.status})`);
      }
      if (!cfgRes.ok) {
        throw new Error((await cfgRes.text()) || `Unable to load field configuration (${cfgRes.status})`);
      }

      const defsPayload = await defsRes.json();
      const cfgPayload = await cfgRes.json();
      const definitions = parseListPayload(defsPayload, ['items', 'data']);
      const existingConfigs = parseListPayload(cfgPayload, ['items', 'data']);
      const byFieldKey = new Map(existingConfigs.map((row) => [row.field_key, row]));

      const rows = definitions.map((def) => {
        const existing = byFieldKey.get(def.field_key);
        const allowedLogics = Array.isArray(def.allowed_logics) ? def.allowed_logics : [];
        const defaultLogic = allowedLogics.length === 1 ? allowedLogics[0] : '';
        return {
          field_key: def.field_key,
          label: def.label,
          is_required: !!def.is_required,
          allowed_logics: allowedLogics,
          lookup_domain: def.lookup_domain || null,
          allow_multiple: !!def.allow_multiple,
          help_text: def.help_text || '',
          config_id: existing?.id || null,
          source_column_name: existing?.source_column_name || '',
          mapping_logic: existing?.mapping_logic || defaultLogic,
          single_value: existing?.single_value || '',
        };
      });
      setConfigRows(rows);
      setConfigInitialSnapshot(buildConfigSnapshot(rows));
      setConfigSearchTerm('');
      setConfigFilter('all');

      const mappingRowsWithConfig = rows.filter((row) => row.mapping_logic === 'mapping' && !!row.config_id);
      if (mappingRowsWithConfig.length > 0) {
        const missingEntries = await Promise.all(
          mappingRowsWithConfig.map(async (row) => {
            try {
              const res = await fetch(`${API_ENDPOINTS.MAPPING_VALUES}?field_config_id=${row.config_id}`);
              if (!res.ok) return [row.field_key, false];
              const payload = await res.json();
              const values = parseListPayload(payload, ['items', 'data']);
              rememberValuesCount(row.field_key, values);
              return [row.field_key, !Array.isArray(values) || values.length === 0];
            } catch {
              return [row.field_key, false];
            }
          })
        );
        setMissingMappingAlertByField(Object.fromEntries(missingEntries));
      } else {
        setMissingMappingAlertByField({});
        setMappingValuesCountByField({});
      }

      // Preload lookup options for rows configured as choice / dimension (by domain, deduplicated).
      const domainsToLoad = new Set();
      for (const row of rows) {
        if (row.mapping_logic === 'choice' || row.mapping_logic === 'dimension') {
          domainsToLoad.add(getLookupDomainForRow(row));
        }
      }
      // The shared 'dimension' domain is always refreshed, as before.
      domainsToLoad.add('dimension');
      for (const domain of domainsToLoad) {
        // eslint-disable-next-line no-await-in-loop
        await ensureLookupOptions(domain, { force: domain === 'dimension' });
      }
    } catch (e) {
      setConfigDialogError(e.message || 'Unable to load field configuration.');
    } finally {
      setConfigDialogLoading(false);
    }
  };

  const handleCloseConfigDialog = () => {
    if (configRows.length > 0 && buildConfigSnapshot(configRows) !== configInitialSnapshot) {
      setConfirmDialogTitle('Discard unsaved changes');
      setConfirmDialogMessage('You have unsaved changes. Close without saving?');
      setConfirmDialogAction(() => async () => {
        resetConfigPanel();
      });
      setConfirmDialogOpen(true);
      return;
    }
    resetConfigPanel();
  };

  const handleConfirmDialogClose = () => {
    if (confirmDialogBusy) return;
    setConfirmDialogOpen(false);
    setConfirmDialogTitle('');
    setConfirmDialogMessage('');
    setConfirmDialogAction(null);
  };

  const handleConfirmDialogConfirm = async () => {
    if (!confirmDialogAction) {
      handleConfirmDialogClose();
      return;
    }
    setConfirmDialogBusy(true);
    try {
      await confirmDialogAction();
      setConfirmDialogOpen(false);
      setConfirmDialogTitle('');
      setConfirmDialogMessage('');
      setConfirmDialogAction(null);
    } finally {
      setConfirmDialogBusy(false);
    }
  };

  const handleConfigRowChange = (fieldKey, fieldName, value) => {
    setConfigRows((prev) =>
      prev.map((row) => {
        if (row.field_key !== fieldKey) return row;
        if (fieldName === 'mapping_logic') {
          const nextLogic = value;
          return {
            ...row,
            mapping_logic: nextLogic,
            // For mapping logic, single_value is not applicable.
            single_value: (nextLogic === 'mapping' || nextLogic === 'move') ? '' : row.single_value,
          };
        }
        return { ...row, [fieldName]: value };
      })
    );
    if (fieldName === 'mapping_logic' && value !== 'mapping') {
      setMissingMappingAlertByField((prev) => ({ ...prev, [fieldKey]: false }));
    }
  };

  const getSingleValuePlaceholder = (mappingLogic) => {
    switch (mappingLogic) {
      case 'mapping':
        return 'Not applicable';
      case 'move':
        return 'Not applicable';
      case 'choice':
        return 'Required*';
      case 'dimension':
        return 'Required*';
      case 'constant':
        return 'Required*';
      case 'format':
        return 'Required*';
      default:
        return '';
    }
  };

  // Dominio di ref_lookup_values per una riga di configurazione:
  // lookup_domain se valorizzato, altrimenti 'dimension' (logica dimension) o field_key (choice/mapping).
  const getLookupDomainForRow = (row) => {
    if (row.lookup_domain) return row.lookup_domain;
    return row.mapping_logic === 'dimension' ? 'dimension' : row.field_key;
  };

  const ensureLookupOptions = async (domain, { force = false } = {}) => {
    if (!domain) return;
    if (!force && lookupOptionsByDomain[domain]) return;
    try {
      const resp = await fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=${encodeURIComponent(domain)}`);
      if (!resp.ok) return;
      const payload = await resp.json();
      const items = parseListPayload(payload, ['items', 'values', 'data']);
      setLookupOptionsByDomain((prev) => ({ ...prev, [domain]: items }));
    } catch {
      // no-op fallback: keep empty options
    }
  };

  const isSingleValueRequiredByLogic = (logic) => (
    logic === 'constant' || logic === 'format' || logic === 'choice' || logic === 'dimension'
  );

  const isRowBlank = (row) => (
    !String(row.source_column_name || '').trim() &&
    !String(row.mapping_logic || '').trim() &&
    !String(row.single_value || '').trim()
  );

  const hasAnyRowInput = (row) => !isRowBlank(row);

  const isRowConfigured = (row) => {
    if (!row.source_column_name.trim() || !row.mapping_logic) return false;
    if (isSingleValueRequiredByLogic(row.mapping_logic)) {
      return !!String(row.single_value || '').trim();
    }
    return true;
  };

  const getRowStatus = (row) => {
    if (isRowConfigured(row)) return { key: 'configured', label: 'Configured', color: 'success' };
    if (hasAnyRowInput(row)) return { key: 'invalid', label: 'Incomplete', color: 'warning' };
    return { key: 'empty', label: 'Not configured', color: 'default' };
  };

  const buildConfigSnapshot = (rows) => JSON.stringify(
    rows.map((row) => ({
      field_key: row.field_key,
      source_column_name: row.source_column_name,
      mapping_logic: row.mapping_logic,
      single_value: row.single_value || '',
      config_id: row.config_id || null,
    }))
  );

  const formatLookupWithGroup = (item) => {
    if (!item) return '';
    const base = item.label || item.value || '';
    return item.group_value ? `${base} (${item.group_value})` : base;
  };

  // silent=true: skip setting configDialogError (caller collects errors itself).
  const handleSaveConfigRow = async (row, { silent = false } = {}) => {
    if (!selectedSheetMapping) return false;
    if (!savedIsMasterService) {
      if (!silent) setConfigDialogError('Master service must be enabled to modify field configuration.');
      return false;
    }
    if (!row.source_column_name.trim() || !row.mapping_logic) {
      if (!silent) setConfigDialogError(`"${row.label}" requires Source Column Name and Mapping Logic.`);
      return false;
    }
    if (!silent) setConfigDialogError(null);
    try {
      const payload = row.config_id
        ? {
            id: row.config_id,
            source_column_name: row.source_column_name.trim(),
            mapping_logic: row.mapping_logic,
            single_value: row.single_value?.trim() || null,
          }
        : {
            sheet_mapping_id: selectedSheetMapping.id,
            field_key: row.field_key,
            source_column_name: row.source_column_name.trim(),
            mapping_logic: row.mapping_logic,
            single_value: row.single_value?.trim() || null,
          };

      const response = await fetch(API_ENDPOINTS.FIELD_CONFIG, {
        method: row.config_id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error((await response.text()) || `Save failed (${response.status})`);
      }

      const savedPayload = await response.json();
      const savedItem = savedPayload?.item || savedPayload;
      setConfigRows((prev) =>
        prev.map((x) =>
          x.field_key === row.field_key
            ? {
                ...x,
                config_id: savedItem?.id || x.config_id,
                source_column_name: savedItem?.source_column_name ?? x.source_column_name,
                mapping_logic: savedItem?.mapping_logic ?? x.mapping_logic,
                single_value: savedItem?.single_value ?? x.single_value,
              }
            : x
        )
      );
      return savedItem || true;
    } catch (e) {
      if (!silent) setConfigDialogError(e.message || `Unable to save the field "${row.label}".`);
      return false;
    }
  };

  const handleSaveAllConfigRows = async ({ finalize = false } = {}) => {
    if (!selectedSheetMapping) return;
    if (!savedIsMasterService) {
      setConfigDialogError('Master service must be enabled to modify field configuration.');
      return;
    }
    setConfigDialogError(null);

    const SINGLE_VALUE_LOGICS = new Set(['constant', 'format', 'choice', 'dimension']);

    // In draft mode: include only rows that have all the fields required by the backend
    // (source_column_name + mapping_logic + single_value when the logic requires it).
    // Rows that aren't filled yet are simply skipped — not treated as errors.
    // In finalize mode: include all required rows and all rows with any input, then validate below.
    const rowsToSave = finalize
      ? configRows.filter((row) => row.is_required || !!String(row.source_column_name || '').trim())
      : configRows.filter((row) => {
          if (!String(row.source_column_name || '').trim()) return false;
          if (!String(row.mapping_logic || '').trim()) return false;
          if (SINGLE_VALUE_LOGICS.has(row.mapping_logic) && !String(row.single_value || '').trim()) return false;
          return true;
        });

    if (rowsToSave.length === 0) {
      setConfigDialogError(
        finalize
          ? 'No fields to save. Configure at least one field before continuing.'
          : 'No fields are ready to be saved. Enter at least Source Column Name and Mapping Logic for one or more fields.'
      );
      return;
    }

    // Full validation only when finalizing (Save & Finalize), not for Save Draft.
    if (finalize) {
      const invalidRequired = configRows.find(
        (row) => row.is_required && !isRowConfigured(row)
      );
      if (invalidRequired) {
        setConfigDialogError(`Required field incomplete: "${invalidRequired.label}".`);
        return;
      }

      const invalidRows = rowsToSave.filter(
        (row) => !String(row.source_column_name || '').trim() || !String(row.mapping_logic || '').trim()
      );
      if (invalidRows.length > 0) {
        const names = invalidRows.map((r) => `"${r.label}"`).join(', ');
        setConfigDialogError(`Source Column Name and Mapping Logic are required for: ${names}.`);
        return;
      }

      const invalidSingleValueRows = rowsToSave.filter(
        (row) => SINGLE_VALUE_LOGICS.has(row.mapping_logic) && !String(row.single_value || '').trim()
      );
      if (invalidSingleValueRows.length > 0) {
        const names = invalidSingleValueRows.map((r) => `"${r.label}"`).join(', ');
        setConfigDialogError(`Single Value is required for: ${names}.`);
        return;
      }
    }

    setConfigDialogSavingAll(true);
    const failedLabels = [];

    // If an optional persisted field has no Source Column Name, remove it from DB.
    // mapping_logic may be preselected, so source_column_name is the real opt-in signal.
    const rowsToDelete = configRows.filter(
      (row) => !!row.config_id && !row.is_required && !String(row.source_column_name || '').trim()
    );
    const deletedFieldKeys = new Set();
    for (const row of rowsToDelete) {
      try {
        const resp = await fetch(API_ENDPOINTS.FIELD_CONFIG, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: row.config_id }),
        });
        if (!resp.ok) {
          failedLabels.push(row.label);
          continue;
        }
        setConfigRows((prev) =>
          prev.map((x) =>
            x.field_key === row.field_key
              ? { ...x, config_id: null, source_column_name: '', mapping_logic: '', single_value: '' }
              : x
          )
        );
        deletedFieldKeys.add(row.field_key);
      } catch {
        failedLabels.push(row.label);
      }
    }

    const savedByFieldKey = {};
    for (const row of rowsToSave) {
      const savedItem = await handleSaveConfigRow(row, { silent: true });
      if (!savedItem) {
        failedLabels.push(row.label);
      } else if (savedItem !== true) {
        savedByFieldKey[row.field_key] = savedItem;
      }
    }
    setConfigDialogSavingAll(false);
    const failed = failedLabels.length;

    if (failed === 0) {
      const finalRows = configRows.map((row) => {
        if (deletedFieldKeys.has(row.field_key)) {
          return { ...row, config_id: null, source_column_name: '', mapping_logic: '', single_value: '' };
        }
        const savedItem = savedByFieldKey[row.field_key];
        if (!savedItem) return row;
        return {
          ...row,
          config_id: savedItem.id || row.config_id,
          source_column_name: savedItem.source_column_name ?? row.source_column_name,
          mapping_logic: savedItem.mapping_logic ?? row.mapping_logic,
          single_value: savedItem.single_value ?? row.single_value,
        };
      });

      // Non-blocking warning: mapping logic selected but mapping values not configured yet.
      const mappingRows = finalRows.filter(
        (row) => row.mapping_logic === 'mapping' && !!row.config_id
      );
      let missingMappingValuesCount = 0;
      const missingByField = {};
      for (const row of mappingRows) {
        try {
          const res = await fetch(`${API_ENDPOINTS.MAPPING_VALUES}?field_config_id=${row.config_id}`);
          if (!res.ok) {
            continue;
          }
          const payload = await res.json();
          const values = parseListPayload(payload, ['items', 'data']);
          const isMissing = !Array.isArray(values) || values.length === 0;
          missingByField[row.field_key] = isMissing;
          rememberValuesCount(row.field_key, values);
          if (isMissing) {
            missingMappingValuesCount += 1;
          }
        } catch {
          // Ignore single-row check failures: this warning is informational only.
        }
      }
      setMissingMappingAlertByField((prev) => ({ ...prev, ...missingByField }));

      setConfigRows(finalRows);
      setConfigDialogError(null);
      setConfigInitialSnapshot(buildConfigSnapshot(finalRows));

      // When finalizing, mark the sheet mapping as is_draft=false.
      let finalizeError = null;
      if (finalize) {
        try {
          const putRes = await fetch(API_ENDPOINTS.SHEET_MAPPING, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: selectedSheetMapping.id,
              file_url: selectedSheetMapping.file_url,
              file_name: selectedSheetMapping.file_name,
              ticketing_system: selectedSheetMapping.ticketing_system,
              is_draft: false,
            }),
          });
          if (putRes.ok) {
            const putData = await putRes.json();
            const updatedMapping = putData.item ?? { ...selectedSheetMapping, is_draft: false };
            setSelectedSheetMapping(updatedMapping);
            setMappingItems((prev) =>
              prev.map((m) => (m.id === updatedMapping.id ? updatedMapping : m))
            );
          } else if (selectedSheetMapping.mapping_type === 'sla') {
            // SLA sheets are validated server-side on finalize (required fields, SLA rule).
            let message = '';
            try {
              const errPayload = await putRes.json();
              message = errPayload?.error || '';
            } catch {
              message = '';
            }
            finalizeError = message || `Unable to finalize the SLA mapping (${putRes.status}).`;
          }
        } catch {
          // Non-blocking: fields are saved; only the draft flag update failed.
        }
      }

      if (finalizeError) {
        setConfigDialogError(finalizeError);
        setMappingToast({
          open: true,
          message: `Fields saved, but the mapping is still in draft. ${finalizeError}`,
          severity: 'warning',
        });
      } else if (missingMappingValuesCount > 0) {
        setMappingToast({
          open: true,
          message: `Configuration saved. ${missingMappingValuesCount} field(s) use Mapping logic but have no Mapping Values configured yet.`,
          severity: 'warning',
        });
      } else {
        setMappingToast({
          open: true,
          message: finalize ? 'Configuration finalized successfully.' : 'Draft saved successfully.',
          severity: 'success',
        });
      }
    } else {
      const names = failedLabels.map((l) => `"${l}"`).join(', ');
      setMappingToast({
        open: true,
        message: `Unable to save ${failedLabels.length} field(s): ${names}. Check the data and try again.`,
        severity: 'warning',
      });
    }
  };

  const handleOpenMappingValuesDialog = async (row) => {
    if (row.mapping_logic !== 'mapping') {
      const msg = `"${row.label}" must use "Mapping" logic to configure mapping values.`;
      setConfigDialogError(msg);
      setMappingToast({ open: true, message: msg, severity: 'warning' });
      return;
    }

    let rowForValues = row;
    if (!rowForValues.config_id) {
      const savedItem = await handleSaveConfigRow(rowForValues);
      if (!savedItem || !savedItem.id) {
        const msg = `Cannot open Mapping Values for "${row.label}". Fill in Source Column Name first.`;
        setConfigDialogError(msg);
        setMappingToast({ open: true, message: msg, severity: 'warning' });
        return;
      }
      rowForValues = {
        ...rowForValues,
        config_id: savedItem.id,
        source_column_name: savedItem.source_column_name ?? rowForValues.source_column_name,
        mapping_logic: savedItem.mapping_logic ?? rowForValues.mapping_logic,
        single_value: savedItem.single_value ?? rowForValues.single_value,
      };
    }

    setSelectedConfigRowForValues(rowForValues);
    setMappingValueForm({ id: null, source_value: '', normalized_ref_id: '' });
    setMappingValues([]);
    setNormalizedOptionsForValues([]);
    setMappingValuesError(null);
    setMappingValuesDialogOpen(true);
    setMappingValuesLoading(true);
    try {
      const [valuesRes, normalizedRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.MAPPING_VALUES}?field_config_id=${rowForValues.config_id}`),
        fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=${encodeURIComponent(rowForValues.lookup_domain || rowForValues.field_key)}`),
      ]);
      if (!valuesRes.ok) {
        throw new Error((await valuesRes.text()) || `Unable to load mapping values (${valuesRes.status})`);
      }
      if (!normalizedRes.ok) {
        throw new Error((await normalizedRes.text()) || `Unable to load normalized values (${normalizedRes.status})`);
      }
      const valuesPayload = await valuesRes.json();
      const normalizedPayload = await normalizedRes.json();
      const valuesList = parseListPayload(valuesPayload, ['items', 'data']);
      setMappingValues(valuesList);
      rememberValuesCount(rowForValues.field_key, valuesList);
      setMissingMappingAlertByField((prev) => ({
        ...prev,
        [rowForValues.field_key]: !Array.isArray(valuesList) || valuesList.length === 0,
      }));
      setNormalizedOptionsForValues(parseListPayload(normalizedPayload, ['items', 'values', 'data']));
    } catch (e) {
      setMappingValuesError(e.message || 'Unable to load mapping values.');
    } finally {
      setMappingValuesLoading(false);
    }
  };

  const handleSaveMappingValue = async () => {
    if (!selectedConfigRowForValues?.config_id) return;
    if (!savedIsMasterService) {
      setMappingValuesError('Master service must be enabled to modify mapping values.');
      return;
    }
    if (!mappingValueForm.source_value.trim() || !mappingValueForm.normalized_ref_id) {
      setMappingValuesError('Please fill in both Source Value and Normalized Value.');
      return;
    }
    setMappingValuesError(null);
    try {
      const payload = mappingValueForm.id
        ? {
            id: mappingValueForm.id,
            source_value: mappingValueForm.source_value.trim(),
            normalized_ref_id: parseInt(mappingValueForm.normalized_ref_id, 10),
          }
        : {
            field_config_id: selectedConfigRowForValues.config_id,
            source_value: mappingValueForm.source_value.trim(),
            normalized_ref_id: parseInt(mappingValueForm.normalized_ref_id, 10),
          };

      const response = await fetch(API_ENDPOINTS.MAPPING_VALUES, {
        method: mappingValueForm.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error((await response.text()) || `Save failed (${response.status})`);
      }
      const refreshed = await fetch(`${API_ENDPOINTS.MAPPING_VALUES}?field_config_id=${selectedConfigRowForValues.config_id}`);
      if (refreshed.ok) {
        const refreshedPayload = await refreshed.json();
        const refreshedList = parseListPayload(refreshedPayload, ['items', 'data']);
        setMappingValues(refreshedList);
        if (selectedConfigRowForValues?.field_key) {
          rememberValuesCount(selectedConfigRowForValues.field_key, refreshedList);
          setMissingMappingAlertByField((prev) => ({
            ...prev,
            [selectedConfigRowForValues.field_key]: !Array.isArray(refreshedList) || refreshedList.length === 0,
          }));
        }
      }
      setMappingValueForm({ id: null, source_value: '', normalized_ref_id: '' });
    } catch (e) {
      setMappingValuesError(e.message || 'Unable to save the mapping value.');
    }
  };

  const handleEditMappingValue = (item) => {
    setMappingValueForm({
      id: item.id,
      source_value: item.source_value || '',
      normalized_ref_id: String(item.normalized_ref_id || ''),
    });
  };

  const handleDeleteMappingValue = async (itemId) => {
    if (!selectedConfigRowForValues?.config_id) return;
    if (!savedIsMasterService) {
      setMappingValuesError('Master service must be enabled to modify mapping values.');
      return;
    }
    try {
      const response = await fetch(API_ENDPOINTS.MAPPING_VALUES, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemId }),
      });
      if (!response.ok) {
        throw new Error((await response.text()) || `Delete failed (${response.status})`);
      }
      const refreshed = await fetch(`${API_ENDPOINTS.MAPPING_VALUES}?field_config_id=${selectedConfigRowForValues.config_id}`);
      if (refreshed.ok) {
        const refreshedPayload = await refreshed.json();
        const refreshedList = parseListPayload(refreshedPayload, ['items', 'data']);
        setMappingValues(refreshedList);
        if (selectedConfigRowForValues?.field_key) {
          rememberValuesCount(selectedConfigRowForValues.field_key, refreshedList);
          setMissingMappingAlertByField((prev) => ({
            ...prev,
            [selectedConfigRowForValues.field_key]: !Array.isArray(refreshedList) || refreshedList.length === 0,
          }));
        }
      }
    } catch (e) {
      setMappingValuesError(e.message || 'Unable to delete the mapping value.');
    }
  };

  const configHasUnsavedChanges = configRows.length > 0 && buildConfigSnapshot(configRows) !== configInitialSnapshot;
  const normalizedSearch = configSearchTerm.trim().toLowerCase();
  const filteredConfigRows = configRows.filter((row) => {
    const matchesSearch = !normalizedSearch || row.label.toLowerCase().includes(normalizedSearch);
    if (!matchesSearch) return false;

    if (configFilter === 'required') return row.is_required;
    if (configFilter === 'optional') return !row.is_required;
    if (configFilter === 'configured') return isRowConfigured(row);
    if (configFilter === 'not_configured') return !isRowConfigured(row);
    return true;
  });


  // Come all'apertura della scheda classica: si riparte dai dati salvati
  useEffect(() => {
    if (!id) return;
    mappingFormInitializedRef.current = false;
    fetchMappingData();
  }, [id, fetchMappingData]);

  return {
    // data source
    selectedDataSource, savedDataSourceType, isMasterServiceDraft, savedIsMasterService,
    jiraProjectIdDraft, setJiraProjectIdDraft, isEditingJiraProjectIdRef, jiraProjectIdDirtyRef, mappingFormInitializedRef,
    mappingLoading, mappingSaving, mappingError, setMappingError,
    handleDataSourceChange, handleMasterServiceChange, handleSaveDataSource,
    // fogli
    mappingItems, ticketingOptions, dataStructureOptions, mappingLogicOptions,
    mappingDialogOpen, mappingDialogType, editingSheetMappingId, mappingDialogError, mappingDialogForm,
    handleAddFieldMapping, handleAddSlaMapping, handleCloseMappingDialog, handleMappingDialogChange, handleSaveMappingDialog,
    handleEditSheetMapping, handleDeleteSheetMapping,
    // configurazione dei campi
    selectedSheetMapping, configRows, configDialogLoading, configDialogSavingAll, configDialogError, setConfigDialogError,
    configSearchTerm, setConfigSearchTerm, configFilter, setConfigFilter, configHasUnsavedChanges, filteredConfigRows,
    missingMappingAlertByField, mappingValuesCountByField, pinnedHelpFieldKey, setPinnedHelpFieldKey, hoveredHelpFieldKey, setHoveredHelpFieldKey,
    lookupOptionsByDomain, formatHelpOpen, setFormatHelpOpen,
    handleOpenConfigDialog, handleCloseConfigDialog, handleConfigRowChange, handleSaveAllConfigRows,
    getSingleValuePlaceholder, getLookupDomainForRow, ensureLookupOptions, getRowStatus, formatLookupWithGroup,
    // mapping values
    mappingValuesDialogOpen, setMappingValuesDialogOpen, mappingValuesLoading, mappingValuesError, mappingValues,
    selectedConfigRowForValues, normalizedOptionsForValues, mappingValueForm, setMappingValueForm,
    handleOpenMappingValuesDialog, handleSaveMappingValue, handleEditMappingValue, handleDeleteMappingValue,
    // conferma e messaggi
    confirmDialogOpen, confirmDialogTitle, confirmDialogMessage, confirmDialogBusy,
    handleConfirmDialogClose, handleConfirmDialogConfirm, mappingToast, setMappingToast,
  };
}
